import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, ArrowLeft, BookMarked, Trash2, Copy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { handsAiChat, handsAiPlan, handsAiPlanDelete } from "@/lib/handsApi";

/**
 * Ask Jixxeo A.I — ChatGPT-style single chat page.
 * - Free-form thread, mobile-first bubble UI.
 * - Every assistant reply is auto-saved as part of a "Plan" (session).
 * - If the route has :sid, resume that saved plan.
 */
export default function HandsAskAI() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const { sid: routeSid } = useParams();

  const [sessionId, setSessionId] = useState(routeSid || null);
  const [messages, setMessages] = useState([]);
  const [title, setTitle] = useState(routeSid ? "" : "New chat with Jixxeo A.I");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadingSid, setLoadingSid] = useState(!!routeSid);
  const listRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/helpinghands/ask"); return; }
    if (!routeSid) return;
    setLoadingSid(true);
    handsAiPlan(routeSid)
      .then((r) => {
        setTitle(r.plan?.title || "Saved plan");
        setMessages(r.messages || []);
        setSessionId(routeSid);
      })
      .catch(() => { toast.error("Couldn't load that plan"); nav("/helpinghands/ask"); })
      .finally(() => setLoadingSid(false));
  }, [routeSid, user, loading, nav]);

  useEffect(() => {
    // Auto-scroll to the last message whenever the thread grows or a reply
    // streams in.
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  const sendMessage = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    const optimistic = {
      id: `tmp-${Date.now()}`, role: "user", content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    setBusy(true);
    try {
      const r = await handsAiChat(text, sessionId || undefined);
      setSessionId(r.session_id);
      if (r.title) setTitle(r.title);
      setMessages((m) => {
        // swap the optimistic user message with the persisted one, and append
        // the assistant reply
        const filtered = m.filter((x) => x.id !== optimistic.id);
        return [...filtered, r.user_message, r.reply];
      });
      // Push the session id into the URL so refresh keeps the thread
      if (!routeSid) window.history.replaceState(null, "", `/helpinghands/ask/${r.session_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Jixxeo A.I is quiet right now — try again in a moment.");
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setInput(text);
    } finally {
      setBusy(false);
    }
  };

  const startFresh = () => {
    if (busy) return;
    setSessionId(null);
    setMessages([]);
    setTitle("New chat with Jixxeo A.I");
    window.history.replaceState(null, "", "/helpinghands/ask");
  };

  const deleteThisPlan = async () => {
    if (!sessionId) { startFresh(); return; }
    if (!window.confirm("Delete this plan and its whole thread?")) return;
    try { await handsAiPlanDelete(sessionId); toast.success("Plan deleted"); startFresh(); }
    catch { toast.error("Couldn't delete"); }
  };

  return (
    <div data-testid="hands-ask" className="pb-32">
      <div className="flex items-center gap-2 mb-3">
        <Link to="/helpinghands" data-testid="ask-back" className="p-2 -ml-2 rounded-full hover:bg-white/5"><ArrowLeft className="w-4 h-4" /></Link>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-white/40">Jixxeo A.I</div>
          <div className="font-black text-lg truncate" data-testid="ask-title">{title || "New chat with Jixxeo A.I"}</div>
        </div>
        <Link to="/helpinghands/plans" data-testid="ask-plans-btn"
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-[#22C55E]/50">
          <BookMarked className="w-3 h-3" /> My plans
        </Link>
        {sessionId && (
          <button data-testid="ask-delete-btn" onClick={deleteThisPlan}
            className="p-2 rounded-full hover:bg-[#F87171]/10 text-white/50 hover:text-[#F87171]" title="Delete this plan">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {loadingSid ? (
        <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : messages.length === 0 ? (
        <EmptyState onPick={(t) => setInput(t)} />
      ) : (
        <div ref={listRef} className="space-y-4 max-h-[65vh] overflow-y-auto pr-1" data-testid="ask-thread">
          {messages.map((m) => <Bubble key={m.id} msg={m} />)}
          {busy && (
            <div className="flex items-center gap-2 text-white/50 text-xs" data-testid="ask-thinking">
              <div className="w-6 h-6 rounded-full bg-[#22C55E]/15 text-[#22C55E] flex items-center justify-center"><Sparkles className="w-3 h-3" fill="currentColor" /></div>
              <span className="animate-pulse">Jixxeo A.I is thinking…</span>
            </div>
          )}
        </div>
      )}

      <form onSubmit={sendMessage} data-testid="ask-form"
        className="fixed bottom-16 sm:bottom-20 inset-x-0 z-30 bg-gradient-to-t from-[#0A1512] via-[#0A1512]/95 to-transparent pt-6 pb-3 px-3">
        <div className="max-w-4xl mx-auto flex items-end gap-2 bg-[#0F2118] rounded-2xl border border-[#1D3A2E] focus-within:border-[#22C55E]/50 p-2">
          <textarea data-testid="ask-input" rows={1} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Ask about bills, debts, budgeting — anything about your money."
            className="flex-1 bg-transparent px-2 py-2 text-sm resize-none focus:outline-none max-h-32" />
          <button data-testid="ask-send" type="submit" disabled={busy || !input.trim()}
            className="shrink-0 w-10 h-10 rounded-full bg-[#22C55E] text-[#0A1512] flex items-center justify-center font-black disabled:opacity-40 disabled:cursor-not-allowed">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <div className="max-w-4xl mx-auto mt-1 text-[10px] text-white/40 text-center">
          Jixxeo A.I gives friendly guidance, not legal, tax, or investment advice.
        </div>
      </form>
    </div>
  );
}

function Bubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div data-testid={`ask-msg-${msg.role}`} className={"flex gap-2 " + (isUser ? "justify-end" : "justify-start")}>
      {!isUser && (
        <div className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-[#22C55E] to-[#0F5132] text-[#0A1512] flex items-center justify-center">
          <Sparkles className="w-4 h-4" fill="currentColor" />
        </div>
      )}
      <div className={"max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap border " + (
        isUser ? "bg-[#22C55E] text-[#0A1512] border-[#22C55E] rounded-br-md"
               : "bg-[#0F2118] text-[#E7F5EE] border-[#1D3A2E] rounded-bl-md"
      )}>
        {msg.content}
        {!isUser && (
          <button data-testid="ask-copy" onClick={() => { navigator.clipboard.writeText(msg.content); toast.success("Copied to clipboard"); }}
            className="mt-2 inline-flex items-center gap-1 text-[10px] text-white/50 hover:text-white uppercase tracking-widest">
            <Copy className="w-2.5 h-2.5" /> Copy plan
          </button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onPick }) {
  const suggestions = [
    "I have $2,400 in credit-card debt at 24% APR and pay $75/mo. What should I do?",
    "I make $4,000/mo. What's a reasonable budget breakdown?",
    "Which subscriptions should I cancel first to save the most?",
    "How do I build an emergency fund on a tight paycheck?",
  ];
  return (
    <div data-testid="ask-empty" className="rounded-3xl bg-gradient-to-br from-[#22C55E]/15 via-[#0F2118] to-[#0A1512] border border-[#22C55E]/30 p-6 sm:p-8 text-center">
      <div className="mx-auto w-16 h-16 rounded-2xl bg-[#22C55E] text-[#0A1512] flex items-center justify-center mb-4">
        <Sparkles className="w-8 h-8" fill="currentColor" />
      </div>
      <div className="font-black text-2xl">Ask Jixxeo A.I anything about your money</div>
      <div className="text-sm text-white/60 mt-2 max-w-md mx-auto">
        Bills, debt, budgets, subscriptions — describe your situation and get a personal plan back. Every plan is auto-saved so you can come back to it.
      </div>
      <div className="mt-6 grid gap-2 max-w-lg mx-auto">
        {suggestions.map((s, i) => (
          <button key={i} data-testid={`ask-sugg-${i}`} onClick={() => onPick(s)}
            className="text-left text-xs px-4 py-3 rounded-2xl bg-[#0F2118] border border-[#1D3A2E] hover:border-[#22C55E]/50 transition-colors text-white/80">
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
